/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import * as THREE from 'three'
import React, { Fragment, Suspense, useEffect, useRef, useState, PropsWithChildren } from 'react'
import { Canvas, useFrame, useGraph, useLoader, useThree, Vector3 } from '@react-three/fiber'

import styles from '../styles/preview.module.scss'

import { OrbitControls, PerspectiveCamera, Environment, Edges, Decal, Html, View } from '@react-three/drei'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import ErrorBoundary from '../helpers/error'
import { LibraryData, ModelData, StraussLogoType } from '../store/interfaces'
import { Camera, CanvasTexture, LessEqualDepth, Object3D, TextureLoader } from 'three'
import { IsDarkColor } from '../helpers/helpers'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import {ReactComponent as IconPlus} from '../icons/plus.svg';
import { OverlayTrigger, Tooltip } from 'react-bootstrap'


type PerspectiveCameraProps = JSX.IntrinsicElements['perspectiveCamera'];

export interface PreviewCallbacks {
    createScreenshot: (() => void) | null;
}

interface PreviewProps {
    xOffset: number;
    yOffset?: number;
    cameraScale?: number;
    modelData?: ModelData;
    model: string;
    library: LibraryData;
    baseColorId: string;
    contrastColorId: string;
    straussLogo: StraussLogoType;
    showPlanes: boolean;
    selectedPlanes?: number[];
    allowedPlanes?: number[];
    filledPlanes?: number[];
    thumbnail?: boolean;
    waitBeforeShow?: number;
    isRight?: boolean;
    disableZoom?: boolean;
    showDetails?: boolean;
    showSplashLogo?: boolean;
    callbacks?: React.MutableRefObject<PreviewCallbacks>;
    onPlaneSelected?: (planeId: number) => void;    
}

interface ScreenshotProps {
    name: string;
    takeScreenshot: boolean;
    canvasRef?: React.MutableRefObject<HTMLCanvasElement>;
    onScreenshotTaken: () => void;    
}

interface PreviewContainerModelProps extends PreviewProps {
    divRef: React.MutableRefObject<HTMLDivElement>;
}

interface PreviewContainerProps {
    className: string;
    eventRef: React.MutableRefObject<HTMLDivElement>;
    canvasRef?: React.MutableRefObject<HTMLCanvasElement>;
    scrollRef?: React.MutableRefObject<HTMLDivElement>;
}

interface PreviewModelProps {
    model: string;
    baseColor: string;
    contrastColor: string;
    useContrastForFlaps: boolean;
    straussLogo: StraussLogoType;
    showPlanes: boolean;
    selectedPlanes?: number[];
    allowedPlanes?: number[];
    filledPlanes?: number[];
    showDetails?: boolean;
    showSplashLogo?: boolean;
    onPlaneSelected?: (planeId: number) => void;
}

interface SyncScrollProps {
    scrollDiv: React.MutableRefObject<HTMLDivElement>;
    canvas: React.MutableRefObject<HTMLCanvasElement>;
}

interface PlaneDecalTextureProps {
    id: number;
    textColor: string;
}

interface CameraProps extends PerspectiveCameraProps {
    target: Vector3;
    xOffset: number;
    yOffset: number;
    autoRotateOnStart: boolean;
}

function padLeft(num: number, size: number) {
    let numString: String = num.toString();
    while (numString.length < size) numString = "0" + numString;
    return numString;
}

const VECTOR_UP = new THREE.Vector3(0, 1, 0);

//@ts-ignore
window.USER_MOVED_CAMERA = false;

function CustomCamera(props: CameraProps) {
    const camera = useRef<THREE.PerspectiveCamera>(null!);
    const basePosition = useRef<THREE.Vector3>(new THREE.Vector3());
    const baseTarget = useRef<THREE.Vector3>(new THREE.Vector3());
    const currentTime = useRef(0);

    useEffect(() => {
        currentTime.current = THREE.MathUtils.lerp(-4.0, 0.0, Math.random());
        const targetArray = props.target as number[];
        baseTarget.current.set(targetArray[0], targetArray[1], targetArray[2]);        
    }, [props.target]);

 
    useFrame((state, delta) => {
        if(camera.current !== null) {
            camera.current.setViewOffset(state.size.width, state.size.height, props.xOffset, props.yOffset, state.size.width, state.size.height);
            
            //@ts-ignore
            if(props.autoRotateOnStart && window.USER_MOVED_CAMERA !== true) {
                const maxTime = 5.0;
                const startTime = 1.0;
                const timeBetween = 20.0;

                if(currentTime.current < startTime) {
                    basePosition.current.copy(camera.current.position).sub(baseTarget.current); 
                }

                currentTime.current += delta; 
    
                if(currentTime.current >= startTime && currentTime.current <= maxTime) {
                    const timeAlpha = Math.min(Math.max((currentTime.current - startTime) / (maxTime - startTime), 0.0), 1.0);    
                    const timeSine = Math.sin(THREE.MathUtils.smoothstep(timeAlpha, 0, 1) * Math.PI * 2.0);  
                    const angle = timeSine * Math.PI * 0.25;  
                    camera.current.position.copy(basePosition.current).applyAxisAngle(VECTOR_UP, angle).add(baseTarget.current);     
                    camera.current.lookAt(baseTarget.current); 
                }
                if(currentTime.current > maxTime) {
                    currentTime.current = -(timeBetween - startTime - Math.random() * 10.0);
                }
            }
        }
    });
    return (
        //@ts-ignore
        <PerspectiveCamera {...props} args={[]} makeDefault ref={camera} near={0.01} far={100}>
            <directionalLight color="#ffffff" intensity={0.8} position={[-25, 12.5, 50]} />
            <directionalLight color="#ffffff" intensity={1} position={[50, 10, -30]} />
        </PerspectiveCamera>
    )
}

function Loader() {
    const loadingMesh = React.useRef<THREE.Mesh>(null!);
    
    useFrame(({ clock }) => {
        if(loadingMesh.current !== undefined) {
            loadingMesh.current.rotation.y = clock.getElapsedTime()
        }
      })

    return (
      <mesh ref={loadingMesh} position={[0, 0, 0]}>
        <icosahedronGeometry args={[0.1, 1]}/>
        <meshStandardMaterial color="#999999" flatShading={true}/>
      </mesh>
    )
}

function PlaneDecalTexture(props: PlaneDecalTextureProps) { 
    const { gl } = useThree();
    const canvasRef = useRef(document.createElement("canvas"));
    const context = useRef(canvasRef.current.getContext("2d"));
    const textureRef = useRef<CanvasTexture|null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const canvasSize = 64;
    const textSize = 28;
    if(canvasRef.current) {
        canvasRef.current.width = canvasSize;
        canvasRef.current.height = canvasSize;
    }
  
    useFrame(() => {
        if(!isInitialized) {
            let ctx = context.current;
            if (ctx) {
                ctx.clearRect(0, 0, canvasSize, canvasSize);
    
                const text = String(props.id);
                ctx.font = textSize + 'px sans-serif';
                ctx.textAlign = 'center';
                ctx.fillStyle = '#' + props.textColor;
                
                ctx.fillText(text, 32, 32 + textSize / 2, canvasSize);
            }
            if (textureRef.current) {
                textureRef.current.anisotropy = gl.capabilities.getMaxAnisotropy();
                textureRef.current.minFilter = THREE.LinearMipmapLinearFilter;
                textureRef.current.needsUpdate = true;
                setIsInitialized(true);
            }
        }
    })

    return <canvasTexture
        ref={textureRef}
        attach="map"
        image={canvasRef.current} 
    />
}

const UpdatingPopover = React.forwardRef(
    //@ts-ignore
    ({ onCreate, onDestroy, popper, children, show: _, ...props }, ref) => {
        useEffect(() => {
            onCreate(popper);
            return () => {
                onDestroy(popper);
            }
        }, [popper, children, onCreate, onDestroy]);

        return (
            //@ts-ignore
            <Tooltip className={styles.tooltip} ref={ref} {...props}>
                {children}
            </Tooltip>
        );
    },
);

function PreviewModel(props: PreviewModelProps) {
    const {t} = useTranslation();
    const { gl, scene, camera } = useThree();
    let glb = useLoader(GLTFLoader, `models/${props.model}.glb`);

    //const [hoveredElement, setHoveredElement] = useState(-1);
    const [isKneeVisible, setIsKneeVisible] = useState(true);
    const [isZipperVisible, setIsZipperVisible] = useState(true);    
    const [isJacketZipperVisible, setIsJacketZipperVisible] = useState(true);
    const { nodes } = useGraph(glb.scene) as any;
    const [baseColor] = useState({value: new THREE.Color(props.baseColor)});
    const [contrastColor] = useState({value: new THREE.Color(props.contrastColor)});
    const [flapColor] = useState({value: new THREE.Color(props.useContrastForFlaps ? props.contrastColor : props.baseColor)});
    const meshRef = React.useRef<THREE.Mesh>(null!);
    const kneeRef = React.useRef<HTMLDivElement>(null!);

    const baseColorUniform = new THREE.Color(props.baseColor);
    const contrastColorUniform = new THREE.Color(props.contrastColor);
    const flapColorUniform = new THREE.Color(props.useContrastForFlaps ? props.contrastColor : props.baseColor);

    let hoveredElement = -1;
    let planeMaterials: any = [];
    //useEffect(() => {
    //    document.body.style.cursor = hoveredElement !== -1 ? 'pointer' : 'auto'
    //    return () => {
    //        document.body.style.cursor = 'auto';
    //    }
    //}, [hoveredElement])
    
    let popperInstance: any = null;

    
    const planes = [];
    for(const nodeName in nodes) {
        if(nodeName.startsWith('Plane_')) {
            planes.push(nodes[nodeName]);
        }
    };

    let baseNode = nodes['Mesh'];
    let innerNode = nodes['MeshInside'];
    let labelNode = nodes['Label'];
    let logoNode = nodes['Logo'];
    
    let logoRed = nodes['LogoRed'];
    let logoBlack = nodes['LogoBlack'];
    let logoWhite = nodes['LogoWhite'];

    let detailKnee = nodes['DetailKnee'];
    let detailZipper = nodes['DetailZipper'];
    let detailJacketZipper = nodes['DetailJacketZipper'];

    let splashLogo = nodes['LogoSplash'];

    if(logoRed) {
        logoRed.visible = (props.straussLogo === StraussLogoType.Standard) || (!logoBlack && !logoWhite);
    }
    if(logoBlack) {
        logoBlack.visible = (props.straussLogo === StraussLogoType.StitchedBlack);
    }
    if(logoWhite) {
        logoWhite.visible = (props.straussLogo === StraussLogoType.StitchedWhite);
    }

    const markerNode = nodes['Marker'];
    const baseMaterial = baseNode.material;
    const innerMaterial = innerNode?.material;
    const splashMaterial = splashLogo?.material;


    const initMaterial = function(mat: any) {
        if (mat.map) {
            mat.map.anisotropy = gl.capabilities.getMaxAnisotropy();
            mat.map.minFilter = THREE.LinearMipmapLinearFilter;
            mat.map.needsUpdate = true;
        }
    
        if (mat.normalMap) {
            mat.normalMap.anisotropy = gl.capabilities.getMaxAnisotropy();
            mat.normalMap.minFilter = THREE.LinearMipmapLinearFilter;
            mat.normalMap.needsUpdate = true;
        }
    
        mat.metalness = 0.2;
        mat.roughness = 0.8;
        
        mat.onBeforeCompile = (shader: THREE.Shader, renderer: THREE.Renderer) => {
            shader.uniforms.baseColor = {value: baseColorUniform};
            shader.uniforms.contrastColor = {value: contrastColorUniform};
            shader.uniforms.flapColor = {value: flapColorUniform};
    
            shader.fragmentShader = 
            shader.fragmentShader.replace( 
              '#include <emissivemap_pars_fragment>', 
              `
                uniform vec3 baseColor;
                uniform vec3 contrastColor;
                uniform vec3 flapColor;
    
                #ifdef USE_EMISSIVEMAP
                    uniform sampler2D emissiveMap;
                #endif
    
                vec3 blendOverlay(vec3 base, vec3 blend) {
                    return mix(1.0 - 2.0 * (1.0 - base) * (1.0 - blend), 2.0 * base * blend, step(base, vec3(0.5)));
                }
              `
            ).replace( 
              '#include <map_fragment>', 
              `
                vec3 detailMap = vec3(0, 0, 0);
                #ifdef USE_MAP
                    vec4 sampledDiffuseColor = texture2D( map, vUv );
                    detailMap = sampledDiffuseColor.rgb;
                    diffuseColor = vec4(baseColor, sampledDiffuseColor.a);
                #endif
              `
            ).replace( 
                '#include <emissivemap_fragment>', 
                `
                float blendId1 = 0.0;
                float blendId2 = 0.0;
                float blendId3 = 0.0;
                float blendId4 = 0.0;
                float blendId5 = 0.0;
                float blendId6 = 0.0;
                
                #ifdef USE_EMISSIVEMAP
                    vec4 idMap1 = texture2D( emissiveMap, vUv );
                    blendId1 = idMap1.g;
                    blendId4 = idMap1.b;
                    blendId5 = idMap1.r;
    
                    totalEmissiveRadiance = vec3(0, 0, 0);
                #endif
                #ifdef USE_AOMAP
                    vec4 idMap2 = texture2D( aoMap, vUv );
                    blendId2 = idMap2.b;
                    blendId3 = idMap2.r;
                    blendId6 = idMap2.g;
                #endif
    
                vec3 zipperColor = contrastColor;
    
                diffuseColor.rgb = mix(diffuseColor.rgb, zipperColor, blendId1);
                diffuseColor.rgb = mix(diffuseColor.rgb, mix(baseColor, vec3(0, 0, 0), 0.3), blendId5);
                roughnessFactor = mix(roughnessFactor, 0.7, blendId5);
                roughnessFactor = mix(roughnessFactor, 0.6, blendId3);
    
    
                diffuseColor.rgb = mix(diffuseColor.rgb, contrastColor, blendId2);
                diffuseColor.rgb = mix(diffuseColor.rgb, zipperColor, blendId4);
                diffuseColor.rgb = mix(diffuseColor.rgb, flapColor, blendId3);
    
                diffuseColor.rgb = blendOverlay(diffuseColor.rgb, detailMap);
                diffuseColor.rgb = mix(diffuseColor.rgb, sampledDiffuseColor.rgb, blendId6);
                `
            ).replace( 
              '#include <aomap_fragment>', 
              ``
            );
    
            mat.userData.shader = shader;
        };
    
    }

    initMaterial(baseMaterial);
    if(innerMaterial) {
        initMaterial(innerMaterial);
    }
    if(splashMaterial) {
        initMaterial(splashMaterial);
        splashMaterial.alphaTest = false;
    }
    
    gl.compile(scene, camera);

    const basePlaneMaterial = new THREE.MeshStandardMaterial({color: IsDarkColor(props.baseColor) ? '#ffffff' :  '#000000', side: THREE.DoubleSide, transparent: true, opacity: 0.33});
    const filledMaterial = new THREE.MeshStandardMaterial({color: '#008800', side: THREE.DoubleSide, transparent: true, opacity: 0.8});
    const selectedMaterial = new THREE.MeshStandardMaterial({color: '#66cc66', side: THREE.DoubleSide, transparent: true, opacity: 0.8});

    useEffect(() => {
        baseColor.value = new THREE.Color(props.baseColor);
        contrastColor.value = new THREE.Color(props.contrastColor);        
        flapColor.value = new THREE.Color(props.useContrastForFlaps ? props.contrastColor : props.baseColor);    
    }, [props.baseColor, props.contrastColor, props.useContrastForFlaps, baseColor, contrastColor, flapColor, baseMaterial]);
    
    useFrame(({ clock }) => {
        const delta = clock.getElapsedTime()
        if(baseMaterial.userData.shader) {
            const alpha = Math.min(0.5, Math.max(0.2, delta * 6.0));
            baseMaterial.userData.shader.uniforms.baseColor.value.lerp(baseColor.value, alpha);
            baseMaterial.userData.shader.uniforms.contrastColor.value.lerp(contrastColor.value, alpha);
            baseMaterial.userData.shader.uniforms.flapColor.value.lerp(flapColor.value, alpha);
        }
        if(innerMaterial && innerMaterial.userData.shader) {
            const alpha = Math.min(0.5, Math.max(0.2, delta * 6.0));
            innerMaterial.userData.shader.uniforms.baseColor.value.lerp(baseColor.value, alpha);
            innerMaterial.userData.shader.uniforms.contrastColor.value.lerp(contrastColor.value, alpha);
            innerMaterial.userData.shader.uniforms.flapColor.value.lerp(flapColor.value, alpha);
        }
        planeMaterials.forEach((planeMaterial:any) => {
            const protoMaterial = (props.filledPlanes?.includes(planeMaterial.id) ? filledMaterial : (props.selectedPlanes?.includes(planeMaterial.id) ? selectedMaterial : basePlaneMaterial));
            planeMaterial.mat.color = protoMaterial.color;
            planeMaterial.mat.opacity = protoMaterial.opacity;
        });

        if(hoveredElement !== -1 && document.body.style.cursor !== 'pointer') {
            document.body.style.cursor = 'pointer';
        } else if(hoveredElement === -1 && document.body.style.cursor !== 'auto') {
            document.body.style.cursor = 'auto';
        }

        if(popperInstance !== null) {
            popperInstance.scheduleUpdate();
        }
    })
    const v1 = new THREE.Vector3();

    return (
        <Fragment>
            <mesh
                ref={meshRef}
                geometry={baseNode.geometry}
                material={baseMaterial}
                position={baseNode.position}
                rotation={baseNode.rotation}
                scale={baseNode.scale}
                onPointerEnter={(evt) => {
                    evt.stopPropagation();
                }}
                onClick={(evt) => {
                    evt.stopPropagation();
                }}>
            </mesh>
            {innerNode && <mesh geometry={innerNode.geometry} material={innerMaterial} position={innerNode.position} rotation={innerNode.rotation} scale={innerNode.scale}></mesh>}
            {labelNode && <mesh geometry={labelNode.geometry} material={labelNode.material} position={labelNode.position} rotation={labelNode.rotation} scale={labelNode.scale}></mesh>}
            {logoNode && <mesh geometry={logoNode.geometry} material={logoNode.material} position={logoNode.position} rotation={logoNode.rotation} scale={logoNode.scale}></mesh>}

            {logoRed && logoRed.visible && <mesh geometry={logoRed.geometry} material={logoRed.material} position={logoRed.position} rotation={logoRed.rotation} scale={logoRed.scale}></mesh>}
            {logoBlack && logoBlack.visible && <mesh geometry={logoBlack.geometry} material={logoBlack.material} position={logoBlack.position} rotation={logoBlack.rotation} scale={logoBlack.scale}></mesh>}
            {logoWhite && logoWhite.visible && <mesh geometry={logoWhite.geometry} material={logoWhite.material} position={logoWhite.position} rotation={logoWhite.rotation} scale={logoWhite.scale}></mesh>}
            
            {props.showSplashLogo && splashLogo && <mesh geometry={splashLogo.geometry} material={splashMaterial} position={splashLogo.position} rotation={splashLogo.rotation} scale={splashLogo.scale}></mesh>}

            {props.showDetails && detailKnee && meshRef.current !== null &&
                <object3D position={detailKnee.position}>
                    <Html
                        wrapperClass={styles.detailInfoDiv} 
                        zIndexRange={[10, 0]} 
                        calculatePosition={(el: Object3D, camera: Camera, size: { width: number; height: number }) => {
                            const objectPos = v1.setFromMatrixPosition(el.matrixWorld)
                            objectPos.project(camera)
                            const widthHalf = size.width / 2
                            const heightHalf = size.height / 2
            
                            let posX = objectPos.x * widthHalf + widthHalf;
                            let posY = -(objectPos.y * heightHalf) + heightHalf;
                            let rightBorder = 150;
                            if(posX > size.width - rightBorder) {
                                posX = size.width - rightBorder;
                            }
                            
                            if(posX < 0) {
                                posX = 0;
                            }
                            return [posX, posY];
                        }} 
                        occlude={[meshRef]} 
                        onOcclude={(visible) => {setIsKneeVisible(!visible); return null;}}
                        style={{
                            transition: 'all 0.5s',
                            opacity: isKneeVisible ? 1 : 0,
                            transform: `scale(${isKneeVisible ? 1 : 0.5})`
                        }}
                        ref={kneeRef}
                    >
                        {isKneeVisible && <OverlayTrigger
                            placement="bottom"
                            flip={true}
                            overlay={  
                                //@ts-ignore
                                <UpdatingPopover onCreate={(popper) => {popperInstance = popper;}} onDestroy={() => {popperInstance = null;}}>
                                    <span dangerouslySetInnerHTML={{__html: t('editscreen.additionalProductInfos.knee')}}></span>
                                </UpdatingPopover>
                            }>
                            <div><IconPlus/></div>
                        </OverlayTrigger>}
                    </Html>
                </object3D>
            }
            {props.showDetails && detailZipper && meshRef.current !== null &&
                <object3D position={detailZipper.position}>
                    <Html
                        wrapperClass={styles.detailInfoDiv} 
                        zIndexRange={[10, 0]} 
                        calculatePosition={(el: Object3D, camera: Camera, size: { width: number; height: number }) => {
                            const objectPos = v1.setFromMatrixPosition(el.matrixWorld)
                            objectPos.project(camera)
                            const widthHalf = size.width / 2
                            const heightHalf = size.height / 2
            
                            let posX = objectPos.x * widthHalf + widthHalf;
                            let posY = -(objectPos.y * heightHalf) + heightHalf;
                            let rightBorder = 150;
                            if(posX > size.width - rightBorder) {
                                posX = size.width - rightBorder;
                            }
                            
                            if(posX < 0) {
                                posX = 0;
                            }
                            return [posX, posY];
                        }} 
                        occlude={[meshRef]} 
                        onOcclude={(visible) => {setIsZipperVisible(!visible); return null;}}
                        style={{
                            transition: 'all 0.5s',
                            opacity: isZipperVisible ? 1 : 0,
                            transform: `scale(${isZipperVisible ? 1 : 0.5})`
                        }} 
                    >
                        {isZipperVisible && <OverlayTrigger
                            placement="bottom"
                            overlay={
                                //@ts-ignore
                                <UpdatingPopover onCreate={(popper) => {popperInstance = popper;}} onDestroy={() => {popperInstance = null;}}>
                                    <span dangerouslySetInnerHTML={{__html: t('editscreen.additionalProductInfos.zipper')}}></span>
                                </UpdatingPopover>
                            }
                            >
                            <div><IconPlus/></div>
                        </OverlayTrigger>}
                    </Html>
                </object3D>
            }
            {props.showDetails && detailJacketZipper && meshRef.current !== null &&
                <object3D position={detailJacketZipper.position}>
                    <Html
                        wrapperClass={styles.detailInfoDiv} 
                        zIndexRange={[100, 0]} 
                        calculatePosition={(el: Object3D, camera: Camera, size: { width: number; height: number }) => {
                            const objectPos = v1.setFromMatrixPosition(el.matrixWorld)
                            objectPos.project(camera)
                            const widthHalf = size.width / 2
                            const heightHalf = size.height / 2
            
                            let posX = objectPos.x * widthHalf + widthHalf;
                            let posY = -(objectPos.y * heightHalf) + heightHalf;
                            let rightBorder = 150;
                            if(posX > size.width - rightBorder) {
                                posX = size.width - rightBorder;
                            }
                            
                            if(posX < 0) {
                                posX = 0;
                            }
                            return [posX, posY];
                        }} 
                        occlude={[meshRef]} 
                        onOcclude={(visible) => {setIsJacketZipperVisible(!visible); return null;}}
                        style={{
                            transition: 'all 0.5s',
                            opacity: isJacketZipperVisible ? 1 : 0,
                            transform: `scale(${isJacketZipperVisible ? 1 : 0.5})`
                        }} 
                    >
                        {isJacketZipperVisible && <OverlayTrigger
                            placement="bottom"
                            overlay={
                                //@ts-ignore
                                <UpdatingPopover onCreate={(popper) => {popperInstance = popper;}} onDestroy={() => {popperInstance = null;}}>
                                    <span dangerouslySetInnerHTML={{__html: t('editscreen.additionalProductInfos.jacketzipper')}}></span>
                                </UpdatingPopover>
                            }
                            >
                            <div><IconPlus/></div>
                        </OverlayTrigger>}
                    </Html>
                </object3D>
            }
            {
                props.showPlanes && markerNode && 
                <object3D position={markerNode.position} rotation={markerNode.rotation} scale={markerNode.scale}>
                    {
                        planes.map((node, nodeIndex) => {
                            const planeId = parseInt(node.name.split('_')[1]);
                            
                            if(props.allowedPlanes === undefined || !props.allowedPlanes.includes(planeId)) {
                                return null;
                            }

                            const planeMat = basePlaneMaterial.clone();
                            planeMaterials.push({id: planeId, mat: planeMat});

                            return (
                                <mesh 
                                    key={nodeIndex}
                                    geometry={node.geometry}
                                    material={planeMat}
                                    position={node.position}
                                    rotation={node.rotation}
                                    renderOrder={2 + nodeIndex}
                                    scale={node.scale}
                                    userData={{planeId}}
                                    onPointerEnter={(evt) => {
                                        if(props.onPlaneSelected !== undefined) {
                                            hoveredElement = evt.eventObject.userData.planeId;
                                            evt.stopPropagation()
                                        }
                                    }}
                                    onPointerLeave={(evt) => {
                                        if(props.onPlaneSelected !== undefined) {
                                            if(hoveredElement === evt.eventObject.userData.planeId) {
                                                hoveredElement = -1;
                                            }
                                            evt.stopPropagation()
                                        }
                                    }}
                                    onPointerMove={(evt) => {
                                        if(props.onPlaneSelected !== undefined) {
                                            if(evt.intersections.length > 0) {
                                                let hitPlaneId = evt.eventObject.userData.planeId;
                                                if(hitPlaneId !== undefined) {
                                                    hoveredElement = hitPlaneId;
                                                } else {
                                                    hoveredElement = -1;
                                                }
                                            } else {
                                                hoveredElement = -1;
                                            }
                                        }
                                    }}
                                    onClick={(evt) => {
                                        if(props.onPlaneSelected !== undefined) {
                                            props.onPlaneSelected(planeId);
                                        }
                                        evt.stopPropagation()
                                    }}
                                >
                                    <Edges
                                        scale={1}
                                        threshold={1000}
                                        color={basePlaneMaterial.color}
                                    />
                                    <Decal
                                        //debug // Makes "bounding box" of the decal visible
                                        position={[0, 0, 0]} // Position of the decal
                                        rotation={[0, 0, 0]} // Rotation of the decal (can be a vector or a degree in radians)
                                        scale={[0.05, 0.05, 0.1]} // Scale of the decal
                                    >
                                        <meshBasicMaterial 
                                            alphaTest={0.5}
                                            depthWrite={true}
                                            depthTest={true}
                                            depthFunc={LessEqualDepth} 
                                            polygonOffset={true}
                                            polygonOffsetFactor={-10.0}
                                            polygonOffsetUnits={-10.0}
                                        >
                                            <PlaneDecalTexture id={planeId} textColor={basePlaneMaterial.color.getHexString()}/>
                                        </meshBasicMaterial>
                                    </Decal>
                                    {/*
                                        <Text color="black" anchorX="center" anchorY="middle" renderOrder={3} rotation={[0, Math.PI, 0]} fontSize={0.02} position={[0, 0, 0]} castShadow={false} depthOffset={-1}>
                                            {planeId}
                                        </Text>
                                    */}
                                </mesh>
                            )
                        })
                    }
                </object3D>
            }
        </Fragment>
    );
}

function Screenshot(props: ScreenshotProps) {
    let renderedFrames = 0;
    let screenshotTaken = false;
    useEffect(() => {
        renderedFrames = 0;
        screenshotTaken = false;
    }, [props.takeScreenshot]);

    useFrame(() => {
        if(props.takeScreenshot && renderedFrames > 3 && props.canvasRef !== undefined && !screenshotTaken) {
            const link = document.createElement('a');
            link.setAttribute('download', props.name);
            link.setAttribute('href', props.canvasRef.current.toDataURL('image/png').replace('image/png', 'image/octet-stream'));
            link.click();
            
            screenshotTaken = true;
            props.onScreenshotTaken();
        }
        ++renderedFrames;
    });

    return null;
}

export function Preview(props: PreviewProps) {
    const selectedBaseColor = props.library.baseColors.find((value) => {return value.id === props.baseColorId;});
    const selectedBaseColorValue = selectedBaseColor ? (selectedBaseColor.modelColor !== undefined ? selectedBaseColor.modelColor : selectedBaseColor.color) : '#ffffff';
    const selectedContrastColor = props.library.contrastColors.find((value) => {return value.id === props.contrastColorId;});
    const selectedContrastColorValue = selectedContrastColor ? (selectedContrastColor.modelColor !== undefined ? selectedContrastColor.modelColor : selectedContrastColor.color) : '#ffffff';
    const canvasRef = useRef<HTMLCanvasElement>(null!);

    const [captureScreenshot, setCaptureScreenshot] = useState(false);

    let cameraPosition: Vector3 = (props.modelData !== undefined && props.modelData.cameraStartPosition !== undefined) ? props.modelData.cameraStartPosition as Vector3 : [-3, 0, 2];
    if (props.isRight && props.modelData !== undefined && props.modelData.cameraStartPositionRight !== undefined) {
        cameraPosition = props.modelData.cameraStartPositionRight as Vector3;
    }
    let cameraTarget: Vector3 = (props.modelData !== undefined && props.modelData.cameraTargetPosition !== undefined) ? props.modelData.cameraTargetPosition as Vector3 : [0, 0, 0];
    if (props.isRight && props.modelData !== undefined && props.modelData.cameraTargetPositionRight !== undefined) {
        cameraTarget = props.modelData.cameraTargetPositionRight as Vector3;
    }
    let minDistance: number = (props.modelData !== undefined && props.modelData.minDistance !== undefined) ? props.modelData.minDistance : 1.5;
    let maxDistance: number = (props.modelData !== undefined && props.modelData.maxDistance !== undefined) ? props.modelData.maxDistance : 1.8;
    const minPolarAngle: number = (props.modelData !== undefined && props.modelData.minPolarAngle !== undefined) ? props.modelData.minPolarAngle : 1.35;
    const maxPolarAngle: number = (props.modelData !== undefined && props.modelData.maxPolarAngle !== undefined) ? props.modelData.maxPolarAngle : 1.7;
    const modelname = props.thumbnail ? props.model + '_Thumbnail' : (isMobile ? props.model + '_mobile' : props.model);

    if(props.cameraScale !== undefined) {
        minDistance *= props.cameraScale;
        maxDistance *= props.cameraScale;
    }

    if(isMobile) {
        minDistance *= 0.5;
    }

    if(props.callbacks) {
        props.callbacks.current = {
            createScreenshot: () => {
                setCaptureScreenshot(true);
            }
        }
    }
    const now = new Date();
    
    return (
        <Canvas
            ref={canvasRef}
            className={styles.canvas}
            gl={{
                alpha: true,
                debug: {checkShaderErrors: process.env.NODE_ENV !== 'production'},
                preserveDrawingBuffer: (props.callbacks !== null)
            }}
            frameloop={props.thumbnail ? "demand" : "always"}
            dpr={captureScreenshot ? 5 : ((isMobile && props.thumbnail) ? Math.min(window.devicePixelRatio, 2) : Math.min(Math.max(window.devicePixelRatio, 2), 3))}
        
        >
            {
                window.SALES &&
                <Screenshot takeScreenshot={captureScreenshot} canvasRef={canvasRef} onScreenshotTaken={() => {setCaptureScreenshot(false);}} name={`No${props.model}_B${props.baseColorId}_C${props.contrastColorId}_${now.getFullYear()}_${padLeft(now.getMonth() + 1, 2)}_${padLeft(now.getDate(), 2)}_${padLeft(now.getHours(), 2)}_${padLeft(now.getMinutes(), 2)}.png`}></Screenshot>
            }
            <CustomCamera position={cameraPosition} target={cameraTarget} autoRotateOnStart={!props.thumbnail} xOffset={props.xOffset} yOffset={props.yOffset !== undefined ? props.yOffset : 0}></CustomCamera>
            <OrbitControls minPolarAngle={minPolarAngle} maxPolarAngle={maxPolarAngle} minDistance={minDistance} maxDistance={maxDistance} autoRotate={false} autoRotateSpeed={0.2} target={cameraTarget} enablePan={false} enableZoom={props.thumbnail !== true && !props.disableZoom} rotateSpeed={isMobile ? 1.8 : 1.4} onStart={() => {
                //@ts-ignore
                window.USER_MOVED_CAMERA = true;
            }}/>
            <Environment files={`models/environment.hdr`} />
            <directionalLight intensity={0.3} position={[-5, 2, 5]}/>
            <directionalLight intensity={0.5} position={[5, 5, 5]}/>
            <Suspense fallback={<Loader/>}>
                <ErrorBoundary errorMessage={null} key={`model-error-${modelname}`}>
                    <PreviewModel model={modelname} baseColor={selectedBaseColorValue} contrastColor={selectedContrastColorValue} useContrastForFlaps={props.baseColorId === '06'} straussLogo={props.straussLogo} showPlanes={props.showPlanes} filledPlanes={props.filledPlanes} selectedPlanes={props.selectedPlanes} allowedPlanes={props.allowedPlanes} onPlaneSelected={props.onPlaneSelected} showDetails={props.showDetails} showSplashLogo={props.showSplashLogo}/>
                </ErrorBoundary>
            </Suspense>
        </Canvas>
    );
};


export function PreviewContainer(props: PropsWithChildren<PreviewContainerProps>) {
    const canvasRef = useRef<HTMLCanvasElement>(null!);
    const usedCanvasRef = props.canvasRef ? props.canvasRef : canvasRef;

    return <Canvas
        gl={{
            alpha: true,
            debug: {checkShaderErrors: process.env.NODE_ENV !== 'production'}
        }}
        frameloop="always"
        dpr={isMobile ? Math.min(window.devicePixelRatio, 2) : Math.min(Math.max(window.devicePixelRatio, 2), 3)}
        className={props.className}
        eventSource={props.eventRef}
        ref={usedCanvasRef}
        onScroll={() => { 
            if(props.scrollRef !== undefined) {
                //Note(daniel): Align views directly after scroll to remove rubberbanding.
                if(props.scrollRef.current && usedCanvasRef.current) {
                    const scroll = props.scrollRef.current.scrollTop;
                    const canvasElement = usedCanvasRef.current.parentElement?.parentElement;
                    if(canvasElement) {
                        canvasElement.style.transform = `translateY(${scroll}px)`;
                    }
                }
            }
        }}
    >
        {props.children}
        {(props.scrollRef !== undefined) && <SyncScroll scrollDiv={props.scrollRef} canvas={usedCanvasRef}/>}
    </Canvas>
}

export function PreviewContainerModel(props: PreviewContainerModelProps) {
    const selectedBaseColor = props.library.baseColors.find((value) => {return value.id === props.baseColorId;});
    const selectedBaseColorValue = selectedBaseColor ? (selectedBaseColor.modelColor !== undefined ? selectedBaseColor.modelColor : selectedBaseColor.color) : '#ffffff';
    const selectedContrastColor = props.library.contrastColors.find((value) => {return value.id === props.contrastColorId;});
    const selectedContrastColorValue = selectedContrastColor ? (selectedContrastColor.modelColor !== undefined ? selectedContrastColor.modelColor : selectedContrastColor.color) : '#ffffff';
   
    let cameraPosition: Vector3 = (props.modelData !== undefined && props.modelData.cameraStartPosition !== undefined) ? props.modelData.cameraStartPosition as Vector3 : [-3, 0, 2];
    if (props.isRight && props.modelData !== undefined && props.modelData.cameraStartPositionRight !== undefined) {
        cameraPosition = props.modelData.cameraStartPositionRight as Vector3;
    }
    let cameraTarget: Vector3 = (props.modelData !== undefined && props.modelData.cameraTargetPosition !== undefined) ? props.modelData.cameraTargetPosition as Vector3 : [0, 0, 0];
    if (props.isRight && props.modelData !== undefined && props.modelData.cameraTargetPositionRight !== undefined) {
        cameraTarget = props.modelData.cameraTargetPositionRight as Vector3;
    }
    let minDistance: number = (props.modelData !== undefined && props.modelData.minDistance !== undefined) ? props.modelData.minDistance : 1.5;
    let maxDistance: number = (props.modelData !== undefined && props.modelData.maxDistance !== undefined) ? props.modelData.maxDistance : 1.8;
    const minPolarAngle: number = (props.modelData !== undefined && props.modelData.minPolarAngle !== undefined) ? props.modelData.minPolarAngle : 1.35;
    const maxPolarAngle: number = (props.modelData !== undefined && props.modelData.maxPolarAngle !== undefined) ? props.modelData.maxPolarAngle : 1.7;
    const modelname = props.thumbnail ? props.model + '_Thumbnail' : (isMobile ? props.model + '_mobile' : props.model);

    if(props.cameraScale !== undefined) {
        minDistance *= props.cameraScale;
        maxDistance *= props.cameraScale;
    }

    if(isMobile) {
        minDistance *= 0.5;
    }
    // @ts-ignore
    return (
        <View track={props.divRef}>
            <CustomCamera position={cameraPosition} target={cameraTarget} autoRotateOnStart={!props.thumbnail} xOffset={0} yOffset={0}></CustomCamera>
            <OrbitControls minPolarAngle={minPolarAngle} maxPolarAngle={maxPolarAngle} minDistance={minDistance} maxDistance={maxDistance} autoRotate={false} autoRotateSpeed={0.2} target={cameraTarget} enablePan={false} enableZoom={props.thumbnail !== true && !props.disableZoom} rotateSpeed={isMobile ? 1.8 : 1.4} onStart={() => {
                //@ts-ignore
                window.USER_MOVED_CAMERA = true;
            }}/>
            <Environment files={`models/environment.hdr`} />
            <directionalLight intensity={0.3} position={[-5, 2, 5]}/>
            <directionalLight intensity={0.5} position={[5, 5, 5]}/>
            <Suspense fallback={<Loader/>}>
                <ErrorBoundary errorMessage={null} key={`model-error-${modelname}`}>
                    <PreviewModel model={modelname} baseColor={selectedBaseColorValue} contrastColor={selectedContrastColorValue} useContrastForFlaps={props.baseColorId === '06'} straussLogo={props.straussLogo} showPlanes={props.showPlanes} filledPlanes={props.filledPlanes} selectedPlanes={props.selectedPlanes} allowedPlanes={props.allowedPlanes} onPlaneSelected={props.onPlaneSelected} showDetails={props.showDetails} showSplashLogo={props.showSplashLogo}/>
                </ErrorBoundary>
            </Suspense>
        </View>
    );
};

export function SyncScroll(props: SyncScrollProps) {
    //Note(daniel): Align views directly after scroll to remove rubberbanding.
    useFrame(() => {
        if(props.scrollDiv.current && props.canvas.current) {
            const scroll = props.scrollDiv.current.scrollTop;
            const canvasElement = props.canvas.current.parentElement?.parentElement;
            if(canvasElement) {
                canvasElement.style.transform = `translateY(${scroll}px)`;
            }
        }
    });

    return null;
};

useLoader.preload(TextureLoader, '/models/environment.hdr')